import alt from "../alt";
import indexBy from "lodash/keyBy";
import extend from "lodash/extend";
import BoxOfficeUtil from "../utils/BoxOfficeUtil";
import BoxOfficeActions from "../actions/BoxOfficeActions";
import EventBucketActions from "../actions/EventBucketActions";
import GlobalNotificationActions from "../actions/GlobalNotificationActions";
import UserStore from "../stores/UserStore";
import EventBucketStore from "../stores/EventBucketStore";

import { formatPrice } from "../util";

const SHOULD_PERSIST = !!window.localStorage;
const LS_KEY = "eventive-boxOfficeState";
let cachedBoxOfficeState =
  (SHOULD_PERSIST &&
    window.localStorage[LS_KEY] &&
    JSON.parse(window.localStorage[LS_KEY])) ||
  {};

class BoxOfficeStore {
  constructor() {
    this.bindActions(BoxOfficeActions);
    this.state = {
      orderData: {},
      orderInProgress: false,
      orderSuccess: false,
      orderErrorMessage: null,
      lastOrder: null,

      terminal: null,
      terminalConnectionStatus: "not_connected",
      terminalPaymentStatus: "not_ready",

      displayMode: cachedBoxOfficeState.displayMode || "basic", // 'basic' | 'pos'
      posConfig: cachedBoxOfficeState.posConfig || {},

      externalNeedsConfirm: false
    };
  }

  onInitializeTerminal() {
    if (this.state.terminal || !window.StripeTerminal) {
      return;
    }

    const terminal = window.StripeTerminal.create({
      onFetchConnectionToken: BoxOfficeUtil.fetchStripeTerminalToken,
      onUnexpectedReaderDisconnect: () =>
        BoxOfficeActions.terminalUnexpectedDisconnect(),
      onConnectionStatusChange: ({ status }) =>
        BoxOfficeActions.terminalConnectionStatusChange(status),
      onPaymentStatusChange: ({ status }) =>
        BoxOfficeActions.terminalPaymentStatusChange(status)
    });
    this.setState({ terminal });
  }

  onTerminalUnexpectedDisconnect() {
    window.alert(`Eventive Terminal unexpectedly disconnected.`);
  }

  onTerminalConnectionStatusChange(terminalConnectionStatus) {
    this.setState({ terminalConnectionStatus });
  }

  onTerminalPaymentStatusChange(terminalPaymentStatus) {
    this.setState({ terminalPaymentStatus });
  }

  onReset(data) {
    this.setState({
      orderData: Object.assign(
        {
          subitems: [],
          customerType: "standard"
        },
        data
      ),
      orderInProgress: false,
      orderSuccess: false,
      orderErrorMessage: null
    });
  }

  onCancelExternal() {
    this.setState({
      externalNeedsConfirm: false,
      orderInProgress: false,
      orderErrorMessage: "External payment was canceled."
    });
  }

  onUpdateCurrentOrder(data) {
    let haveItemsChanged =
      (data.subitems &&
        data.subitems.length !==
          (this.state.orderData.subitems || []).length) ||
      (data.subitems &&
        data.subitems.some(
          (s, i) => this.state.orderData.subitems[i].quantity !== s.quantity
        ));

    let shouldRecheckCoupon =
        (data.coupon_id && !this.state.orderData.coupon_id) ||
        (data.coupon_code && !this.state.orderData.coupon_code) ||
        ((this.state.orderData.coupon_id || this.state.orderData.coupon_code) &&
          haveItemsChanged),
      shouldRecheckPass =
        (data.pass && !this.state.orderData.pass) ||
        (this.state.orderData.pass && haveItemsChanged);

    if (shouldRecheckCoupon) {
      data.checking_coupon = true;
    }

    if (shouldRecheckPass) {
      data.checking_pass = true;
    }

    // Inject service fee at end if necessary
    let autoServiceFee = EventBucketStore.getEventBucketById(
      this.state.orderData.event_bucket
    ).box_office_service_fee;
    if (data.subitems && autoServiceFee) {
      data.subitems = data.subitems.filter(i => !(i.service_fee && i.auto));
      data.subitems.push({ service_fee: true, auto: true });
    }

    this.setState({
      orderData: Object.assign({}, this.state.orderData, data)
    });

    if (shouldRecheckCoupon) {
      BoxOfficeUtil.testCoupon(
        UserStore.getState().apiKey,
        this.state.orderData
      ).then(result => {
        BoxOfficeActions.updateCurrentOrder({
          coupon: result,
          checking_coupon: false
        });
      });
    }

    if (shouldRecheckPass) {
      BoxOfficeUtil.testPass(
        UserStore.getState().apiKey,
        this.state.orderData
      ).then(result => {
        BoxOfficeActions.updateCurrentOrder({
          pass_data: result,
          checking_pass: false
        });
      });
    }
  }

  async onSubmitOrder() {
    this.setState({
      orderInProgress: true,
      orderErrorMessage: null,
      lastOrder: null
    });

    const orderData = this.state.orderData;

    if (orderData.person) {
      orderData.subitems.forEach(item => {
        if (item.gift_card) {
          item.senderName = orderData.person.details.name;
          item.senderEmail = orderData.person.details.email;
        }
      });
    } else {
      orderData.subitems.forEach(item => {
        if (item.gift_card) {
          item.senderName = orderData.name;
          item.senderEmail = orderData.email;
        }
      });
    }

    // Require external POS to be confirmed
    if (orderData.paymentType === "external" && !externalNeedsConfirm) {
      this.setState({ externalNeedsConfirm: true });
      return;
    } else {
      this.setState({ externalNeedsConfirm: false });
    }

    if (orderData.paymentType === "terminal") {
      try {
        const { order, secret, cart_id, no_payment_required } =
          await BoxOfficeUtil.prepareOrder(
            UserStore.getState().apiKey,
            orderData
          );
        if (!no_payment_required) {
          const result = await this.state.terminal.collectPaymentMethod(secret);
          console.log(
            `Terminal: Payment Method collected, with result:`,
            JSON.stringify(result)
          );
          if (result.error) {
            throw new Error(result.error.message);
          }
          const paymentResult = await this.state.terminal.processPayment(
            result.paymentIntent
          );
          console.log(
            `Terminal: Payment processed, with result:`,
            JSON.stringify(paymentResult)
          );
          if (paymentResult.error) {
            throw new Error(paymentResult.error.message);
          }
          orderData.payment_intent = paymentResult.paymentIntent.id;
          orderData.cart_id = cart_id;
        }
      } catch (e) {
        this.setState({
          orderInProgress: false,
          orderErrorMessage: e.message
        });
        return;
      }
    }

    BoxOfficeUtil.submitOrder(UserStore.getState().apiKey, orderData)
      .then(data => {
        if (data.orders) {
          data = data.orders[0];
        }

        this.setState({
          lastOrder: data
        });

        let autoPopulate = EventBucketStore.getEventBucketById(
          data.event_bucket.id
        ).box_office_auto_populate;
        BoxOfficeActions.reset({
          event_bucket: data.event_bucket.id,
          subitems:
            autoPopulate &&
            orderData.subitems.length === 1 &&
            orderData.subitems[0].ticket_bucket
              ? [
                  {
                    ticket_bucket: orderData.subitems[0].ticket_bucket,
                    quantity: 1
                  }
                ]
              : []
        });

        window.scrollTo(0, 0); // scroll back to top to restart
      })
      .catch(e => {
        this.setState({
          orderInProgress: false,
          orderErrorMessage: e.message
        });
      });
  }

  onSetDisplayMode(displayMode) {
    this.setState({ displayMode });
  }

  onUpdatePosConfig(config) {
    this.setState({ posConfig: { ...this.state.posConfig, ...config } });
  }
}

let store = alt.createStore(BoxOfficeStore, "BoxOfficeStore");

if (SHOULD_PERSIST) {
  store.listen(state => {
    window.localStorage[LS_KEY] = JSON.stringify({
      displayMode: state.displayMode,
      posConfig: state.posConfig
    });
  });
}

export default store;



// WEBPACK FOOTER //
// ./src/app/stores/BoxOfficeStore.js